import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CommentIcon from '@mui/icons-material/Comment';
import CurrencyYenIcon from '@mui/icons-material/CurrencyYen';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import PortraitIcon from '@mui/icons-material/Portrait';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SecurityIcon from '@mui/icons-material/Security';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SimCardAlertIcon from '@mui/icons-material/SimCardAlert';
import StarRateIcon from '@mui/icons-material/StarRate';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Button, Collapse, Stack, Typography } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import { green, grey, red } from '@mui/material/colors';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { unwrapResult } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import * as React from 'react';
import Div100vh from 'react-div-100vh';
import { useDispatch, useSelector } from 'react-redux';

import logo from 'assets/images/appLogo.png';
import { Breadcrumbs } from 'components/Elements/Breadcrumbs';
import { Footer } from 'components/Elements/Footer/Footer';
import { NavItem } from 'components/Elements/Navigate/NavItem';
import { useAuth } from 'lib/auth';
import {
  ADMINISTRATOR_LIST_VIEW,
  ATRS_IMPORT_ERROR_LIST_VIEW,
  COMMENT_LIST_VIEW,
  DASHBOARD_VIEW,
  EXHIBIT_LIST_VIEW,
  INQUIRIES_LIST_VIEW,
  ORDER_LIST_VIEW,
  PAYMENTS_LIST_VIEW,
  PRODUCT_LIST_VIEW,
  PRODUCT_BULK_UPDATE_VIEW,
  REQUEST_LIST_VIEW,
  UNAPPROVED_USER_LIST_VIEW,
  USER_CREATE_VIEW,
  USER_LIST_VIEW,
  SATISFACTION_RATINGS_LIST_VIEW,
  WITHDRAWAL_LIST_VIEW,
} from 'routes/urls';
import { fetchMyAccount, logoutAdministrator, selectMyAccount } from 'stores/administratorSlice';
import { EXTERNAL_ADMIN_SCOPE } from 'utils/const';
import { LAYOUT_CONSTANTS, SIDE_MENU_WIDTH } from 'utils/cssConstants';

const drawerWidth = LAYOUT_CONSTANTS.DRAWER_WIDTH;

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'fixed',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    zIndex: theme.zIndex.drawer,
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const mdTheme = createTheme({
  palette: {
    type: 'light',
    buttonPrimary: {
      main: green[600],
      dark: green[400],
      contrastText: '#FFF',
    },
    grey: {
      main: grey[600],
      dark: grey[400],
      contrastText: '#FFF',
    },
    error: {
      main: red[600],
      dark: red[400],
    },
    partsOne: {
      main: '#3C4B64',
      background: '#F3F3F3',
    },
    safetyGreen: {
      main: '#3C4B64',
      background: '#3ad798',
    },
    errorRed: {
      main: '#3C4B64',
      background: '#f84f56',
    },
    warningYellow: {
      main: '#3C4B64',
      background: '#ffb453',
    },
    disableGray: {
      main: '#3C4B64',
      background: '#c4c4c4',
    },
    white: {
      main: '#FFFFFF',
    },
  },
});

export const MainLayout = ({ children, links, toolbar }) => {
  const { logout, user } = useAuth();
  const dispatch = useDispatch();

  const [open, setOpen] = React.useState(false);

  const [hoverOpen, setHoverOpen] = React.useState(false);

  const [userCollapseOpen, setUserCollapseOpen] = React.useState(false);

  const [productCollapseOpen, setProductCollapseOpen] = React.useState(false);

  const myAccount = useSelector(selectMyAccount);

  const [isLoading, setIsLoading] = React.useState(false);

  const toggleDrawer = () => {
    setOpen((v) => !v);
  };

  const handleClick = async () => {
    setIsLoading(true);
    const params = {
      accessToken: user.accessToken,
    };

    const res = await dispatch(logoutAdministrator(params));

    // ここでAPIエラーの場合は止まる
    unwrapResult(res);

    await logout();
    setIsLoading(false);
  };

  React.useEffect(() => {
    // /account/me は外部管理者も呼べる。
    // MainLayout は画面ごとにマウントされ直すが、取れていれば fetchMyAccount は API を呼ばない (redux に残る)。
    // ログイン・ログアウトで redux の値は消えるので、別の管理者になったときは取り直す
    dispatch(fetchMyAccount());
  }, [dispatch]);

  // @todo Phase2: role ベースの権限制御に変更
  const externalAdminScope = user?.externalAdminScope ?? null;
  const isInternalAdmin = !externalAdminScope;
  const showProductsMenu = isInternalAdmin || externalAdminScope === EXTERNAL_ADMIN_SCOPE.PRODUCTS;
  const showOrdersMenu = isInternalAdmin || externalAdminScope === EXTERNAL_ADMIN_SCOPE.ORDERS;

  const drawerOpen = open || hoverOpen;

  return (
    <ThemeProvider theme={mdTheme}>
      <Div100vh>
        <Box sx={{ display: 'flex', minHeight: '100%', minWidth: '100%' }}>
          <CssBaseline />
          <AppBar position="fixed" color="partsOne">
            <Toolbar
              sx={{
                pr: '24px',
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer}
                sx={{ marginRight: '36px' }}
              >
                <MenuIcon color="white" />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <img height={35} src={logo} srcSet={logo} alt="PARTS ONE inc." loading="lazy" />
              </Box>
              <Stack direction="row" spacing={2} alignItems="center" maxWidth="80%" ml={2}>
                <Stack
                  direction="row"
                  spacing={0}
                  alignItems="center"
                  maxWidth="calc(100% - 120px)"
                >
                  <Typography variant="body2" style={{ color: 'white' }} noWrap>
                    {user?.isExternalAdmin ? user?.supplierName : myAccount?.admin_name}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>
                    (&nbsp;
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }} noWrap>
                    {user?.email}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'white' }}>
                    &nbsp;)
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  color="white"
                  onClick={handleClick}
                  sx={{ m: 1, minWidth: '120px', width: '120px' }}
                  disabled={isLoading}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LogoutIcon />
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      ログアウト
                    </Typography>
                  </Stack>
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>
          <Drawer
            variant="permanent"
            open={drawerOpen}
            onMouseEnter={() => setHoverOpen(true)}
            onMouseLeave={() => setHoverOpen(false)}
          >
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: [1],
              }}
            >
              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon />
              </IconButton>
            </Toolbar>
            <Divider />
            <List component="nav">
              {isInternalAdmin && (
                <>
                  <NavItem link={DASHBOARD_VIEW} icon={<DashboardIcon />} text="ダッシュボード" />
                  <NavItem
                    icon={<PortraitIcon />}
                    subIcon={
                      userCollapseOpen ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowDownIcon style={{ transform: 'rotate(270deg)' }} />
                      )
                    }
                    text="ユーザー管理"
                    onClick={() => setUserCollapseOpen(!userCollapseOpen)}
                    subNav={
                      <Collapse in={userCollapseOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 2 }}>
                          <NavItem
                            link={USER_LIST_VIEW}
                            icon={<PeopleIcon />}
                            text="ユーザー一覧"
                          />
                          <NavItem
                            link={USER_CREATE_VIEW}
                            icon={<GroupAddIcon />}
                            text="ユーザー一括登録"
                          />
                        </List>
                      </Collapse>
                    }
                  />
                  <NavItem
                    link={UNAPPROVED_USER_LIST_VIEW}
                    icon={<HowToRegIcon />}
                    text="承認待ち一覧"
                  />
                </>
              )}

              {showProductsMenu && (
                <NavItem
                  icon={<DirectionsCarIcon />}
                  subIcon={
                    productCollapseOpen ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowDownIcon style={{ transform: 'rotate(270deg)' }} />
                    )
                  }
                  text="商品管理"
                  onClick={() => setProductCollapseOpen(!productCollapseOpen)}
                  subNav={
                    <Collapse in={productCollapseOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        <NavItem
                          link={PRODUCT_LIST_VIEW}
                          icon={<DirectionsCarIcon />}
                          text="商品一覧"
                        />
                        <NavItem
                          link={PRODUCT_BULK_UPDATE_VIEW}
                          icon={<PostAddIcon />}
                          text="商品一括更新"
                        />
                      </List>
                    </Collapse>
                  }
                />
              )}

              {isInternalAdmin && (
                <NavItem link={EXHIBIT_LIST_VIEW} icon={<StorefrontIcon />} text="出品者" />
              )}

              {showOrdersMenu && (
                <NavItem link={ORDER_LIST_VIEW} icon={<ShoppingCartIcon />} text="注文一覧" />
              )}

              {isInternalAdmin && (
                <>
                  <NavItem link={COMMENT_LIST_VIEW} icon={<CommentIcon />} text="コメント一覧" />
                  <NavItem link={PAYMENTS_LIST_VIEW} icon={<CurrencyYenIcon />} text="請求一覧" />
                  <NavItem
                    link={REQUEST_LIST_VIEW}
                    icon={<SupportAgentIcon />}
                    text="部品お問い合わせ一覧"
                  />
                  <NavItem
                    link={INQUIRIES_LIST_VIEW}
                    icon={<MailOutlineIcon />}
                    text="お問い合わせ一覧"
                  />
                  <NavItem
                    link={ADMINISTRATOR_LIST_VIEW}
                    icon={<SecurityIcon />}
                    text="管理者一覧"
                  />
                  <NavItem
                    link={ATRS_IMPORT_ERROR_LIST_VIEW}
                    icon={<SimCardAlertIcon />}
                    text="データ取込エラー"
                  />
                  <NavItem link={WITHDRAWAL_LIST_VIEW} icon={<BlockIcon />} text="退会申請一覧" />
                  <NavItem
                    link={SATISFACTION_RATINGS_LIST_VIEW}
                    icon={<StarRateIcon />}
                    text="満足度評価一覧"
                  />
                </>
              )}
            </List>
          </Drawer>
          <Box
            component="main"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              backgroundColor: (theme) => theme.palette.grey[100],
              minHeight: '100%',
              overflowY: 'scroll',
              marginLeft: {
                xs: SIDE_MENU_WIDTH.XS,
                sm: SIDE_MENU_WIDTH.SM,
              },
              transition: (theme) =>
                theme.transitions.create('margin', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
            }}
          >
            <Box sx={{ p: 2 }}>
              <Toolbar />
              {links.length ? <Breadcrumbs links={links} /> : null}
              {toolbar && <Box sx={{ mb: 2 }}>{toolbar}</Box>}
              <Box>{children}</Box>
            </Box>
            <Box
              sx={{
                mt: 'auto',
                whiteSpace: 'nowrap',
                position: 'sticky',
                left: '0',
                minWidth: '100%',
              }}
            >
              <Footer />
            </Box>
          </Box>
        </Box>
      </Div100vh>
    </ThemeProvider>
  );
};

MainLayout.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      name: PropTypes.string.isRequired,
      icon: PropTypes.element,
    }),
  ),
  toolbar: PropTypes.node,
};

MainLayout.defaultProps = {
  links: [],
  toolbar: null,
};
